import { useState, useEffect } from 'react';
import { Flame, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * StreakReminder - Shows a banner in the evening (~6pm local time) if user hasn't scanned today.
 * Helps protect streaks by reminding users to scan before day ends.
 * 
 * Props:
 *   hasScannedToday: boolean - whether user has scanned today
 *   streakDays: number - current streak count
 *   onScanNow: () => void - callback to navigate to scan
 */
export default function StreakReminder({ hasScannedToday, streakDays, onScanNow }) {
  const [dismissed, setDismissed] = useState(false);
  const [showReminder, setShowReminder] = useState(false);

  useEffect(() => {
    // Check if it's reminder time (between 6pm and 11pm local time)
    const checkTime = () => {
      const hour = new Date().getHours();
      const isEveningTime = hour >= 18 && hour < 23;
      
      // Only show if:
      // 1. It's evening time
      // 2. User hasn't scanned today
      // 3. User has an active streak (> 0)
      // 4. Not dismissed this session
      const shouldShow = isEveningTime && !hasScannedToday && !dismissed && streakDays > 0;
      setShowReminder(shouldShow);
    };

    checkTime();
    
    // Check every 5 minutes
    const interval = setInterval(checkTime, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [hasScannedToday, streakDays, dismissed]);

  // Also check dismissed state from sessionStorage to persist during session
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem('streak_reminder_dismissed');
    if (wasDismissed === 'true') {
      setDismissed(true);
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('streak_reminder_dismissed', 'true');
  };

  if (!showReminder) return null;

  const message = streakDays > 0 
    ? `Don't break your ${streakDays}-day streak!`
    : "Start a new streak today!";

  return (
    <div 
      className="bg-gradient-to-r from-qc-amber/20 to-qc-red/20 border border-qc-amber/40 rounded-2xl p-4 mb-4"
      data-testid="streak-reminder"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-qc-amber/20 border border-qc-amber/40 flex items-center justify-center flex-shrink-0">
          <Flame className="w-5 h-5 text-qc-amber" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-qc-text mb-1">{message}</p>
          <p className="text-xs text-qc-text-dim mb-3">
            Scan something before midnight to keep your streak alive.
          </p>
          <div className="flex items-center gap-2">
            <Button
              onClick={onScanNow}
              size="sm"
              data-testid="streak-reminder-scan"
              className="h-8 px-4 bg-qc-amber hover:bg-qc-amber/80 text-qc-bg rounded-full font-bold text-xs"
            >
              Scan Now
            </Button>
            <button
              onClick={handleDismiss}
              data-testid="streak-reminder-dismiss"
              className="text-xs text-qc-text-faint hover:text-qc-text-dim"
            >
              Remind me later
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          data-testid="streak-reminder-close"
          className="text-qc-text-faint hover:text-qc-text-dim p-1"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
